import React, { useEffect, useMemo, useState } from 'react';
import 'bulma/css/bulma.min.css';
import { getWebrtcCredentials } from '../../../services';
import useWebRTC from '../../../hooks/useWebRTC';

const REGISTRATION_LABELS = {
  idle: 'Não registrado',
  registering: 'Registrando...',
  registered: 'Registrado com sucesso',
  error: 'Erro no registro',
};

function WebRTC() {
  const [credentials, setCredentials] = useState(null);
  const [loadError, setLoadError] = useState('');
  const [destination, setDestination] = useState('');
  const [callHistory, setCallHistory] = useState([]);
  const [autoAnswerEnabled, setAutoAnswerEnabled] = useState(false);
  const [autoAnswerTime, setAutoAnswerTime] = useState(10);

  const autoAnswer = useMemo(
    () => ({ enabled: autoAnswerEnabled, seconds: autoAnswerTime }),
    [autoAnswerEnabled, autoAnswerTime]
  );

  const {
    registrationStatus,
    incomingSession,
    currentSession,
    remoteAudioRef,
    needsUserGesture,
    audioHealthy,
    makeCall,
    answer,
    hangUp,
    unlockAudio,
  } = useWebRTC({ credentials, autoAnswer });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { status, data } = await getWebrtcCredentials();
      if (cancelled) return;
      if (status !== 200) {
        setLoadError((data && data.error) || 'Não foi possível carregar as credenciais do ramal WEB.');
        return;
      }
      const host = window.location.host;
      const hostname = window.location.hostname;
      setCredentials({
        resource: data.resource,
        password: data.password,
        wssUrl: `wss://${host}${data.wssPath}`,
        sipDomain: hostname,
      });
    })();
    return () => { cancelled = true; };
  }, []);

  const handleMakeCall = async () => {
    if (!destination) return;
    await makeCall(destination);
    setCallHistory((prev) => [
      ...prev,
      { type: 'Saída', to: destination, time: new Date().toLocaleString() },
    ]);
  };

  const handleAnswer = async () => {
    const from = incomingSession && incomingSession.remoteIdentity && incomingSession.remoteIdentity.uri
      ? incomingSession.remoteIdentity.uri.toString()
      : '';
    await answer();
    if (from) {
      setCallHistory((prev) => [
        ...prev,
        { type: 'Entrada', from, time: new Date().toLocaleString() },
      ]);
    }
  };

  return (
    <div className="section">
      <div className="container">
        <h1 className="title">Cliente WebRTC - Asterisk (PJSIP)</h1>

        <audio ref={remoteAudioRef} autoPlay playsInline />

        <div className="notification is-info">
          <p>
            <strong>Ramal:</strong> {credentials ? credentials.resource : '—'}
          </p>
          <p>
            <strong>Status do Registro:</strong> {REGISTRATION_LABELS[registrationStatus] || registrationStatus}
          </p>
          {loadError && <p className="has-text-danger"><strong>Erro:</strong> {loadError}</p>}
        </div>

        {needsUserGesture && (
          <div className="notification is-warning">
            <p>O navegador bloqueou a reprodução automática do áudio.</p>
            <button className="button is-warning" onClick={unlockAudio}>
              Clique para ouvir
            </button>
          </div>
        )}

        {!audioHealthy && currentSession && (
          <div className="notification is-danger">
            Sem áudio recebido nos últimos segundos. Verifique sua conexão.
          </div>
        )}

        <div className="box">
          <div className="field">
            <label className="checkbox">
              <input
                type="checkbox"
                checked={autoAnswerEnabled}
                onChange={() => setAutoAnswerEnabled(!autoAnswerEnabled)}
              />
              &nbsp;Atender automaticamente
            </label>
          </div>
          {autoAnswerEnabled && (
            <div className="field is-grouped">
              <label className="label">Tempo (segundos):</label>
              <div className="control">
                <input
                  type="number"
                  className="input"
                  value={autoAnswerTime}
                  onChange={(e) => setAutoAnswerTime(Number(e.target.value))}
                  style={{ width: '100px' }}
                />
              </div>
            </div>
          )}
        </div>

        <div className="field has-addons">
          <div className="control is-expanded">
            <input
              type="text"
              className="input"
              placeholder="Digite a extensão para chamar"
              value={destination}
              onChange={(e) => setDestination(e.target.value)}
            />
          </div>
          <div className="control">
            <button
              onClick={handleMakeCall}
              disabled={!!currentSession || registrationStatus !== 'registered'}
              className="button is-primary"
            >
              Chamar
            </button>
          </div>
          <div className="control">
            <button onClick={hangUp} disabled={!currentSession} className="button is-danger">
              Desligar
            </button>
          </div>
        </div>

        {incomingSession && (
          <div className="notification is-info">
            <p>Chamada recebida de: {incomingSession.remoteIdentity.uri.toString()}</p>
            <button onClick={handleAnswer} className="button is-success">
              Atender
            </button>
          </div>
        )}

        <h2 className="title is-4">Histórico de Chamadas</h2>
        {callHistory.length ? (
          <ul>
            {callHistory.map((call, index) => (
              <li key={index}>
                {call.type === 'Entrada'
                  ? `Chamada recebida de: ${call.from}`
                  : `Chamada realizada para: ${call.to}`}
                {' '}- {call.time}
              </li>
            ))}
          </ul>
        ) : (
          <p>Nenhuma chamada registrada.</p>
        )}
      </div>
    </div>
  );
}

export default WebRTC;
